import { Controller } from '@hotwired/stimulus'
import { useStore } from 'stimulus-store'
import { deviceTypeStore } from '../stores/device_types/device_type_store'
import { escape } from 'lodash'

/**
 * Testing Task Cards Controller
 *
 * Dynamically populates testing task cards on device add and update pages.
 * Subscribes to device type store and automatically fetches relevant testing
 * tasks when the device type changes. On update pages, pre-populates cards
 * with existing device testing task statuses.
 *
 * Connects to data-controller="testing-task-cards"
 */
export default class extends Controller {

    static stores = [
        deviceTypeStore
    ]

    static targets = [
        'container'
    ]

    static values = {
        initialDeviceType: String,  // Device type ID for update page initialization
        existingStatuses: Object    // Map of task IDs to status IDs for pre-selection
    }

    /**
     * Lifecycle: Connect
     *
     * Subscribe to device type store and load initial data if present.
     * On update pages, this will fetch testing tasks for the existing device type.
     */
    connect() {
        useStore(this)

        // Initialize existingStatuses if not present (ensure it's an object)
        // Only set to empty object if the value is truly not provided
        if (!this.hasExistingStatusesValue) {
            this.existingStatusesValue = {}
        }

        // If we have an initial device type (update page), fetch tasks immediately
        if (this.hasInitialDeviceTypeValue && this.initialDeviceTypeValue) {
            this.fetchAndRenderTestingTasks(this.initialDeviceTypeValue)
        } else {
            // Show placeholder message for add page
            this.clearTestingTasks()
        }
    }

    /**
     * Lifecycle: Disconnect
     *
     * Clean up store subscriptions when controller is removed from DOM.
     * The useStore utility handles unsubscription automatically.
     */
    disconnect() {
        // useStore handles cleanup automatically
    }

    /**
     * Store Integration: Device type store callback
     *
     * Called automatically by stimulus-store when deviceTypeStore updates.
     * Delegates to updateTestingTasks for actual implementation.
     *
     * @param {Object} event - Device type store state
     */
    onDeviceTypeStoreUpdate(event) {
        this.updateTestingTasks(event)
    }

    /**
     * Store Integration: Handle device type store changes
     *
     * Extracts device type ID from store state and fetches testing tasks.
     * Handles empty/invalid store states by clearing the container.
     * Testing task statuses are preserved and applied based on task ID matching.
     *
     * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5
     *
     * @param {Object} state - Device type store state
     */
    updateTestingTasks(state) {
        // Extract new device type ID
        const newDeviceTypeId = state && state.id ? state.id : null

        // Validate store state and extract device type ID
        if (newDeviceTypeId) {
            // Device type selected: fetch testing tasks
            this.fetchAndRenderTestingTasks(newDeviceTypeId)
        } else {
            // Device type cleared or invalid: show placeholder
            this.clearTestingTasks()
        }
    }

    /**
     * API Integration: Fetch and render testing tasks
     *
     * Makes API request to fetch testing tasks for the given device type,
     * then renders the cards in the container.
     *
     * @param {String} deviceTypeId - Device type definition ID
     */
    async fetchAndRenderTestingTasks(deviceTypeId) {
        try {
            // Construct API URL with device type parameter
            const url = `/api/testing_tasks/list?device_type=${ encodeURIComponent(deviceTypeId) }`

            // Make API request with proper headers
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            })

            // Check for successful response
            if (!response.ok) {
                throw new Error(`Unable to fetch testing tasks. Please try again.`)
            }

            // Parse JSON response
            const data = await response.json()

            // Extract testing tasks from response
            const testingTasks = data.testing_tasks || []

            // Render the testing task cards
            this.renderTestingTasks(testingTasks)
        } catch (error) {
            console.error('Error fetching testing tasks:', error)
            // Clear container and show error message (Requirement 7.3)
            this.clearTestingTasks('error')
        }
    }

    /**
     * Rendering: Render all testing task cards
     *
     * Clears the container and renders a card for each testing task.
     * If no tasks are available, shows appropriate message.
     *
     * @param {Array} testingTasks - Array of testing task objects
     */
    renderTestingTasks(testingTasks) {
        // Clear existing content (Requirement 7.5)
        this.containerTarget.innerHTML = ''

        // Check if we have any tasks
        if (!testingTasks || testingTasks.length === 0) {
            // Show "no tasks" message (Requirement 7.2)
            this.clearTestingTasks('empty')
            return
        }

        // Render each testing task card
        testingTasks.forEach((task) => {
            const cardHtml = this.createTestingTaskCard(task)
            this.containerTarget.insertAdjacentHTML('beforeend', cardHtml)

            // Apply existing status if available (update page)
            // Get the card element we just inserted
            const cardElement = this.containerTarget.querySelector(`[data-testing-task-id="${ task.id }"]`)
            if (cardElement) {
                this.applyExistingStatus(task.id, cardElement)
            }
        })
    }

    /**
     * Rendering: Create single testing task card
     *
     * Generates HTML for a testing task card matching the existing partial design.
     * Includes card header with task name and radio button group for status options.
     *
     * @param {Object} task - Testing task object with id, name, and states
     * @returns {String} HTML string for the card
     */
    createTestingTaskCard(task) {
        const taskName = escape(task.name)
        const statusOptionsHtml = this.renderStatusOptions(task)

        return `
      <div class="card text-center" style="width: 16rem; margin: 0.5rem;" data-testing-task-id="${ task.id }">
        <div class="card-body">
          <h5 class="card-title">${ taskName }</h5>
        </div>
        <div class="btn-group-vertical mb-3" role="group">
          ${ statusOptionsHtml }
        </div>
      </div>
    `
    }

    /**
     * Rendering: Create radio button group for status options
     *
     * Generates radio buttons for each status option in the testing task.
     * Uses default statuses if task has no defined states.
     *
     * @param {Object} task - Testing task object
     * @returns {String} HTML string for radio buttons
     */
    renderStatusOptions(task) {
        // Get status options from task states or use defaults
        /*
         * [
              {
                "order": 1,
                "name": "Not Started",
                "id": "1bf7c5f8-4941-44f4-bec3-1a0c437e8d2a"
              },
              {
                "order": 2,
                "name": "In Progress",
                "id": "85e73595-52b1-459a-b0af-011d06eef5c1"
              },
              {
                "order": 3,
                "name": "Complete",
                "id": "b99b21ea-2318-4e7c-9ead-2bc659075e84"
              }
            ]
         */
        const statusOptions = task?.states

        // Sort the status options by their `order` field
        const sortedStatusOptions = statusOptions.sort((lhs, rhs) => lhs.order - rhs.order)

        // Generate radio button HTML for each status
        return sortedStatusOptions.map((status) => {
            const statusId = status?.id
            const statusName = escape(status?.name)
            const radioId = `${ task.id }_${ statusId }`
            const radioName = `testing_task[${ task.id }]`

            return `
        <input type="radio" 
               class="btn-check" 
               name="${ radioName }" 
               id="${ radioId }" 
               data-task-id="${ task.id }"
               data-status-id="${ statusId }"
               value="${ statusId }"
               autocomplete="off">
        <label class="btn btn-secondary" for="${ radioId }">
          ${ statusName }
        </label>
      `
        }).join('')
    }

    /**
     * Status Pre-selection: Apply existing status to card
     *
     * Matches task IDs to existingStatuses map and sets checked attribute
     * on matching radio buttons. Task IDs are globally unique, so if a task
     * exists in the map, its status is pre-selected regardless of device type.
     *
     * Requirements: 9.3, 9.4, 9.5
     *
     * @param {String} taskId - Testing task ID
     * @param {HTMLElement} cardElement - Card DOM element
     */
    applyExistingStatus(taskId, cardElement) {
        // Check if we have existing statuses
        if (!this.existingStatusesValue || Object.keys(this.existingStatusesValue).length === 0) {
            // No existing statuses - leave all radio buttons unchecked (Requirement 9.5)
            return
        }

        // Check if this task has an existing status
        const existingStatusId = this.existingStatusesValue[taskId]

        if (!existingStatusId) {
            // Task not in the map - leave radio buttons unchecked (Requirement 9.5)
            return
        }

        // Find the radio button matching the existing status
        const radioId = `${ taskId }_${ existingStatusId }`
        const radioButton = cardElement.querySelector(`[id="${ radioId }"]`)

        if (radioButton) {
            // Set checked attribute on matching radio button (Requirement 9.3, 9.4)
            radioButton.checked = true
        } else {
            // Status ID doesn't match any radio button - log warning
            console.warn(`No radio button found for task ${ taskId } with status ${ existingStatusId }`)
        }
    }

    /**
     * Utilities: Clear testing tasks container
     *
     * Clears all cards and displays placeholder message.
     * Called when no device type is selected or on error.
     *
     * @param {String} reason - Reason for clearing: 'empty', 'error', or default (no selection)
     */
    clearTestingTasks(reason = 'no-selection') {
        let message = ''

        if (reason === 'empty' || reason === 'error') {
            // Requirement 7.2 & 7.3: No testing tasks available or API error
            message = 'No testing tasks available for the selected device type'
        } else {
            // Requirement 7.1: No device type selected
            message = 'Select a device type to view available testing tasks'
        }

        // Requirement 7.4: Apply text-muted styling to placeholders
        this.containerTarget.innerHTML = `<p class="text-muted">${ message }</p>`
    }

}
